import { FormProvider } from 'react-hook-form';
import { Modal, ModalFooter, Button, Icon } from '@polichat/flamboyant';
import { useFlags } from 'flagsmith/react';
// Hooks
import { customUseFlags } from 'src/hooks/customUseFlags';
import { useFinishChatModal } from './useFinishChatModal';
import {
  CLUSTER_VALUES,
  useFinishChatForm,
  type OriginFormValue,
} from './useFinishChatForm';
import { useGlotaT } from 'src/hooks/useGlotaTranslation';
// Components
import { OriginForm, FormPartFactory } from './forms';
// CSS Modules
import styles from './styles.module.css';
import { useEffect } from 'react';

export function FinishChatModalFlow() {
  const flag = useFlags(['finish_chat_show_bot']);
  const finish_chat_show_bot = customUseFlags(flag.finish_chat_show_bot);

  const form = useFinishChatForm();
  const origin = form.watch('origin');

  const { isOpen, isLoading, handleClose, handleFinishChat } =
    useFinishChatModal();

  const FormPart = FormPartFactory(origin as OriginFormValue);

  // Traduções
  const titleText = useGlotaT('spa.chat.finishChat.title');
  const subtitleText = useGlotaT('spa.chat.finishChat.subtitle');
  const cancelText = useGlotaT('spa.chat.finishChat.cancel');
  const finishText = useGlotaT('spa.chat.finishChat.finish');
  const finishingText = useGlotaT('spa.chat.finishChat.finishing');

  const onSubmit = (data: any) => {
    handleFinishChat(data);
  };

  const handleCancel = () => {
    form.reset();
    handleClose();
  };

  useEffect(() => {
    if (finish_chat_show_bot) {
      form.setValue('cluster', CLUSTER_VALUES.active);
    }
  }, [finish_chat_show_bot, form.setValue]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      contentClassName={styles.modalContentContainer}
      data-testid="finish-chat-modal"
    >
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={styles.modalContentForm}
          data-testid="finish-chat-form"
        >
          {/* Header */}
          <div className={styles.modalContentHeader}>
            <h2>{titleText}</h2>
            <button
              className={styles.modalContentHeaderClose}
              onClick={handleCancel}
            >
              <Icon icon="poli-icon pi-close-line" size="sm" />
            </button>
          </div>

          {/* Subtitle */}
          <div className={styles.modalContentHeaderSubtitle}>
            {subtitleText}
          </div>

          <div className={styles.modalContentBody}>
            {/* Main content */}
            <OriginForm />
            {FormPart && <FormPart />}
          </div>
          {/* Footer */}
          <ModalFooter
            className={styles.modalContentFooter}
            data-testid="finish-chat-buttons-container"
          >
            <Button
              outline
              color="primary"
              onClick={handleCancel}
              data-testid="finish-chat-cancel-button"
            >
              {cancelText}
            </Button>
            <Button
              color="secondary"
              type="submit"
              disabled={isLoading}
              data-testid="finish-chat-submit-button"
            >
              {isLoading ? finishingText : finishText}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
